import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../store/appStore';

// Import eyes-open images
import eyesOpenNeutral from '../../assets/avatar/eyes-open-neutral.jpg';
import eyesOpenOpenSmall from '../../assets/avatar/eyes-open-open-small.jpg';
import eyesOpenOpenWide from '../../assets/avatar/eyes-open-open-wide.jpg';
import eyesOpenRound from '../../assets/avatar/eyes-open-round.jpg';
import eyesOpenTalking from '../../assets/avatar/eyes-open-talking.jpg';
// eyes-open-wide-smile removed - using talking instead

// Import eyes-closed images
import eyesClosedNeutral from '../../assets/avatar/eyes-closed-neutral.jpg';
import eyesClosedOpenSmall from '../../assets/avatar/eyes-closed-open-small.jpg';
import eyesClosedOpenWide from '../../assets/avatar/eyes-closed-open-wide.jpg';
// eyes-closed-round removed - using talking instead
import eyesClosedSmile from '../../assets/avatar/eyes-closed-smile.jpg';
import eyesClosedTalking from '../../assets/avatar/eyes-closed-talking.jpg';

import styles from './avatar.module.css';

// Map viseme shapes to avatar images (eyes open)
const VISEME_IMAGES_OPEN: Record<string, string> = {
  neutral: eyesOpenNeutral,
  closed: eyesOpenNeutral,
  open_small: eyesOpenOpenSmall,
  open_wide: eyesOpenOpenWide,
  round: eyesOpenRound,
  talking: eyesOpenTalking,
  smile: eyesOpenTalking,  // Use talking instead of wide-smile
};

// Map viseme shapes to avatar images (eyes closed)
const VISEME_IMAGES_CLOSED: Record<string, string> = {
  neutral: eyesClosedNeutral,
  closed: eyesClosedNeutral,
  open_small: eyesClosedOpenSmall,
  open_wide: eyesClosedOpenWide,
  round: eyesClosedTalking,  // Use talking instead of round
  talking: eyesClosedTalking,
  smile: eyesClosedSmile,
};

export const Avatar: React.FC = () => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentMouthShape, setCurrentMouthShape] = useState('neutral');
  const [eyesClosed, setEyesClosed] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const imagesRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const lastVisemeTimeRef = useRef<number>(0);
  const blinkTimerRef = useRef<number | null>(null);
  const blinkAnimationRef = useRef<number | null>(null);

  const isAvatarSpeaking = useAppStore((state) => state.isAvatarSpeaking);
  const isAvatarLoading = useAppStore((state) => state.isAvatarLoading);
  const currentVisemes = useAppStore((state) => state.currentVisemes);
  const audioElement = useAppStore((state) => state.audioElement);

  // Preload all avatar images
  useEffect(() => {
    const images = new Map<string, HTMLImageElement>();
    const allImages = {
      ...VISEME_IMAGES_OPEN,
      ...Object.fromEntries(
        Object.entries(VISEME_IMAGES_CLOSED).map(([key, src]) => [`${key}_closed`, src])
      ),
    };

    const loadPromises = Object.entries(allImages).map(([key, src]) => {
      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          console.log(`Avatar image loaded: ${key}`);
          images.set(key, img);
          resolve();
        };
        img.onerror = () => {
          console.error(`Failed to load avatar image: ${key}`);
          reject(new Error(`Failed to load ${key}`));
        };
        img.src = src;
      });
    });

    Promise.all(loadPromises)
      .then(() => {
        console.log(`All ${Object.keys(allImages).length} avatar images preloaded successfully`);
        imagesRef.current = images;
        setImagesLoaded(true);
      })
      .catch((error) => {
        console.error('Error preloading avatar images:', error);
        imagesRef.current = images;
        setImagesLoaded(true);
      });
  }, []);

  // Natural blinking system
  useEffect(() => {
    const scheduleNextBlink = () => {
      // Random interval between 2-5 seconds for natural blinking
      const nextBlinkDelay = 2000 + Math.random() * 3000;

      blinkTimerRef.current = setTimeout(() => {
        // Perform blink animation (150ms eyes closed)
        setEyesClosed(true);

        blinkAnimationRef.current = setTimeout(() => {
          setEyesClosed(false);
          // Schedule next blink
          scheduleNextBlink();
        }, 150); // Eyes stay closed for 150ms
      }, nextBlinkDelay);
    };

    // Start the blinking cycle
    scheduleNextBlink();

    // Cleanup on unmount
    return () => {
      if (blinkTimerRef.current) {
        clearTimeout(blinkTimerRef.current);
      }
      if (blinkAnimationRef.current) {
        clearTimeout(blinkAnimationRef.current);
      }
    };
  }, []);

  // Animate mouth based on visemes
  useEffect(() => {
    if (!isAvatarSpeaking || !currentVisemes || currentVisemes.length === 0) {
      setCurrentMouthShape('neutral');
      return;
    }

    startTimeRef.current = Date.now();
    lastVisemeTimeRef.current = 0;
    let lastVisemeIndex = -1;

    const animate = () => {
      let elapsed = 0;
      if (audioElement) {
        elapsed = audioElement.currentTime;
      } else {
        elapsed = (Date.now() - startTimeRef.current) / 1000;
      }

      let currentShape = 'neutral';
      let currentVisemeIndex = -1;

      for (let i = 0; i < currentVisemes.length; i++) {
        if (currentVisemes[i].time <= elapsed) {
          // Filter out tongue and teeth visemes - replace with talking
          const visemeShape = currentVisemes[i].shape || 'neutral';
          if (visemeShape === 'tongue' || visemeShape === 'teeth') {
            currentShape = 'talking';
          } else {
            currentShape = visemeShape;
          }
          currentVisemeIndex = i;
        } else {
          break;
        }
      }

      if (currentVisemeIndex !== lastVisemeIndex) {
        lastVisemeIndex = currentVisemeIndex;
        setCurrentMouthShape(currentShape);
        console.log(`Viseme changed: ${currentShape} (time=${elapsed.toFixed(3)}s, index=${currentVisemeIndex})`);
      }

      if (isAvatarSpeaking) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isAvatarSpeaking, currentVisemes, audioElement]);

  // Draw avatar on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 380;
    canvas.height = 480;

    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (!imagesLoaded) {
      return;
    }

    // Select the appropriate image based on mouth shape and eye state
    let imgKey = currentMouthShape;
    if (eyesClosed) {
      imgKey = `${currentMouthShape}_closed`;
    }

    const img = imagesRef.current.get(imgKey) || imagesRef.current.get(currentMouthShape) || imagesRef.current.get('neutral');

    if (!img) {
      console.warn(`Avatar image not found for shape: ${imgKey}`);
      return;
    }

    const drawImage = () => {
      if (img.naturalWidth === 0) {
        requestAnimationFrame(drawImage);
        return;
      }

      const imgAspectRatio = img.naturalWidth / img.naturalHeight;
      const canvasAspectRatio = canvas.width / canvas.height;

      let drawWidth = canvas.width;
      let drawHeight = canvas.height;
      let drawX = 0;
      let drawY = 0;

      if (imgAspectRatio > canvasAspectRatio) {
        drawWidth = drawHeight * imgAspectRatio;
        drawX = (canvas.width - drawWidth) / 2;
      } else {
        drawHeight = drawWidth / imgAspectRatio;
        drawY = (canvas.height - drawHeight) / 2;
      }

      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      try {
        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
      } catch (error) {
        console.error('Error drawing image:', error);
      }

      ctx.fillStyle = '#666666';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(
        isAvatarSpeaking ? t('avatar.speaking') : t('avatar.listening'),
        190,
        465
      );
    };

    drawImage();
  }, [currentMouthShape, eyesClosed, isAvatarSpeaking, imagesLoaded, t]);

  return (
    <div className={styles.avatarContainer}>
      {(!imagesLoaded || isAvatarLoading) && (
        <div className={styles.avatarLoader}>
          <div className={styles.spinner}></div>
          <p>{!imagesLoaded ? t('avatar.loading') : t('avatar.loading')}</p>
        </div>
      )}
      <canvas
        ref={canvasRef}
        className={styles.avatarCanvas}
        style={{ opacity: (imagesLoaded && !isAvatarLoading) ? 1 : 0.3 }}
        title="Neptune - Assistente Virtuale"
      />
      <div className={styles.avatarStatus}>
        <span className={`${styles.statusDot} ${isAvatarSpeaking ? styles.active : (isAvatarLoading ? styles.loading : '')}`} />
        <span>{isAvatarLoading ? t('avatar.loading') : (isAvatarSpeaking ? t('avatar.inConversation') : t('avatar.waiting'))}</span>
      </div>
    </div>
  );
};
